import type {
  Canvas,
  FileRecord,
  InboxItem,
  Skill,
} from "@cohesive/api-client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { computeListSelectionClick } from "./listSelectionClick";
import {
  dedupeRailSelectionResources,
  parseRailSelectionKey,
  railSelectionKey,
  type RailSelectionKind,
  type RailSelectionSource,
} from "./railSelectionKeys";

export {
  dedupeRailSelectionResources,
  parseRailSelectionKey,
  railSelectionKey,
} from "./railSelectionKeys";
export type { RailSelectionKind, RailSelectionSource };

export const RAIL_SELECTION_KEY_ATTR = "data-rail-selection-key";
export const RAIL_SELECTION_ROOT_ATTR = "data-rail-selection-root";
/**
 * Outermost list sibling used for contiguous-selection adjacency.
 * Often a drop-zone wrapper around the painted row.
 */
export const RAIL_LIST_ITEM_ATTR = "data-rail-list-item";
/** Element that receives the contiguous selection outline. */
export const RAIL_CHROME_ATTR = "data-rail-chrome";
/** Marks a selected list-item host (see railSelectionChrome.css). */
export const RAIL_SELECTED_ATTR = "data-rail-selected";

/**
 * Clicks on these targets keep multi-selection (Finder-style). Selectable
 * rows and the selection bar are explicit; interactive chrome
 * (folder/workspace chevrons, accordion headers, "Show more") is covered by
 * `button` / `a` / `[role="button"]`; workspace header chrome by
 * `[data-workspace-row]`.
 */
const RAIL_CLEAR_SELECTION_EXEMPT_SELECTOR = [
  "[data-rail-canvas-row]",
  "[data-rail-chat-row]",
  "[data-rail-file-row]",
  "[data-rail-skill-row]",
  "[data-rail-selection-bar]",
  "[data-workspace-row]",
  "button",
  "a",
  "[role='button']",
].join(", ");

export type RailRegisteredResource =
  | { kind: "canvas"; data: Canvas }
  | { kind: "chat"; data: InboxItem }
  | {
      kind: "file";
      data: FileRecord;
      /**
       * Row originates from "Shared with me" — multi-drag must stamp this per
       * payload so mixed shared/owned selections don't all copy or all move.
       */
      sharedSource?: boolean;
    }
  | { kind: "skill"; data: Skill };

function resourceRegistryKey(kind: RailSelectionKind, id: string): string {
  return `${kind}:${id}`;
}

/** Visible selectable row keys in DOM order within the active rail root. */
export function collectRailSelectionOrder(
  root: ParentNode | null = null,
): string[] {
  const scope =
    root ?? document.querySelector(`[${RAIL_SELECTION_ROOT_ATTR}]`) ?? document;
  return Array.from(scope.querySelectorAll(`[${RAIL_SELECTION_KEY_ATTR}]`))
    .map((el) => el.getAttribute(RAIL_SELECTION_KEY_ATTR))
    .filter((k): k is string => !!k);
}

function sameContents(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Layers that own Escape for dismiss (Radix dialogs/menus/selects/popovers).
 * Exclude `data-state="closed"` so exit animations don't keep blocking clear.
 */
const ESCAPE_OWNING_LAYER_SELECTOR = [
  '[role="dialog"]:not([data-state="closed"])',
  '[role="alertdialog"]:not([data-state="closed"])',
  '[role="menu"]:not([data-state="closed"])',
  '[role="listbox"]:not([data-state="closed"])',
  '[data-radix-menu-content]:not([data-state="closed"])',
  '[data-radix-select-content]:not([data-state="closed"])',
  '[data-radix-popover-content]:not([data-state="closed"])',
].join(", ");

/**
 * Whether Escape should clear rail multi-selection. Returns false when focus
 * is in an editable field, the event was already handled, or an open modal /
 * menu / select / popover should consume Escape instead.
 */
export function shouldClearRailSelectionOnEscape(
  e: Pick<KeyboardEvent, "key" | "defaultPrevented" | "target">,
  root: ParentNode = document,
): boolean {
  if (e.key !== "Escape" || e.defaultPrevented) return false;
  const target = e.target as HTMLElement | null;
  if (target?.closest?.("input, textarea, [contenteditable]")) return false;
  // Focus inside a dismissable layer, or a layer open without focus (common
  // for non-modal rail menus / body-focused after opening a dialog).
  if (target?.closest?.(ESCAPE_OWNING_LAYER_SELECTOR)) return false;
  if (root.querySelector?.(ESCAPE_OWNING_LAYER_SELECTOR)) return false;
  return true;
}

interface RailSelectionContextValue {
  selectedIds: Set<string>;
  orderedIds: string[];
  setOrderedIds: (ids: string[]) => void;
  rowClick: (id: string, e: React.MouseEvent | MouseEvent) => void;
  selectOnly: (id: string) => void;
  clear: () => void;
  clearIfOutside: (e: React.MouseEvent) => void;
  isSelected: (id: string) => boolean;
  selectionCount: number;
  registerResource: (
    kind: RailSelectionKind,
    id: string,
    resource: RailRegisteredResource,
  ) => void;
  unregisterResource: (kind: RailSelectionKind, id: string) => void;
  getSelectedResources: () => RailRegisteredResource[];
}

const RailSelectionContext = createContext<RailSelectionContextValue | null>(
  null,
);

/**
 * Multi-select store for Workspaces / Skills / nested file rows.
 * Finder list rules: plain replace, cmd/ctrl toggle, shift range over
 * visible DOM order. Only one rail renders at a time under the provider.
 */
export function RailSelectionProvider({
  children,
  /** Clear selection when this identity changes (e.g. active rail). */
  resetKey,
}: {
  children: ReactNode;
  resetKey?: string;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [orderedIds, setOrderedIdsState] = useState<string[]>([]);
  const anchorRef = useRef<string | null>(null);
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const orderedIdsRef = useRef(orderedIds);
  orderedIdsRef.current = orderedIds;
  const resourcesRef = useRef(new Map<string, RailRegisteredResource>());

  const setOrderedIds = useCallback((ids: string[]) => {
    setOrderedIdsState((prev) => (sameContents(prev, ids) ? prev : ids));
  }, []);

  useEffect(() => {
    if (resetKey === undefined) return;
    setSelectedIds(new Set());
    anchorRef.current = null;
  }, [resetKey]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    anchorRef.current = null;
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldClearRailSelectionOnEscape(e)) return;
      clear();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clear]);

  const registerResource = useCallback(
    (kind: RailSelectionKind, id: string, resource: RailRegisteredResource) => {
      resourcesRef.current.set(resourceRegistryKey(kind, id), resource);
    },
    [],
  );

  const unregisterResource = useCallback(
    (kind: RailSelectionKind, id: string) => {
      const key = resourceRegistryKey(kind, id);
      const stillSelected = [...selectedIdsRef.current].some((rowKey) => {
        const parsed = parseRailSelectionKey(rowKey);
        return parsed?.kind === kind && parsed.id === id;
      });
      if (!stillSelected) resourcesRef.current.delete(key);
    },
    [],
  );

  const getSelectedResources = useCallback((): RailRegisteredResource[] => {
    const out: RailRegisteredResource[] = [];
    for (const { kind, id } of dedupeRailSelectionResources(
      selectedIdsRef.current,
    )) {
      const resource = resourcesRef.current.get(resourceRegistryKey(kind, id));
      if (resource) out.push(resource);
    }
    return out;
  }, []);

  const rowClick = useCallback(
    (id: string, e: React.MouseEvent | MouseEvent) => {
      if (e.shiftKey) e.preventDefault();
      const liveOrder = collectRailSelectionOrder();
      const order = liveOrder.length > 0 ? liveOrder : orderedIdsRef.current;
      if (liveOrder.length > 0) {
        orderedIdsRef.current = liveOrder;
        setOrderedIdsState((prev) =>
          sameContents(prev, liveOrder) ? prev : liveOrder,
        );
      }
      const { next, anchor } = computeListSelectionClick({
        id,
        e,
        selectedIds: selectedIdsRef.current,
        orderedIds: order,
        anchor: anchorRef.current,
      });
      anchorRef.current = anchor;
      setSelectedIds(next);
    },
    [],
  );

  const selectOnly = useCallback((id: string) => {
    anchorRef.current = id;
    setSelectedIds(new Set([id]));
  }, []);

  const clearIfOutside = useCallback(
    (e: React.MouseEvent) => {
      if (selectedIds.size === 0) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(RAIL_CLEAR_SELECTION_EXEMPT_SELECTOR)) return;
      clear();
    },
    [selectedIds, clear],
  );

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const value = useMemo<RailSelectionContextValue>(
    () => ({
      selectedIds,
      orderedIds,
      setOrderedIds,
      rowClick,
      selectOnly,
      clear,
      clearIfOutside,
      isSelected,
      // Deduped kind+id count — matches getSelectedResources / bulk actions,
      // not raw row keys (Favorites + canonical copies share one resource).
      selectionCount: dedupeRailSelectionResources(selectedIds).length,
      registerResource,
      unregisterResource,
      getSelectedResources,
    }),
    [
      selectedIds,
      orderedIds,
      setOrderedIds,
      rowClick,
      selectOnly,
      clear,
      clearIfOutside,
      isSelected,
      registerResource,
      unregisterResource,
      getSelectedResources,
    ],
  );

  return (
    <RailSelectionContext.Provider value={value}>
      {children}
    </RailSelectionContext.Provider>
  );
}

export function useRailSelection() {
  const ctx = useContext(RailSelectionContext);
  if (!ctx) {
    throw new Error(
      "useRailSelection must be used within RailSelectionProvider",
    );
  }
  return ctx;
}

export function useRailSelectionOptional() {
  return useContext(RailSelectionContext);
}

/**
 * Resolves whether a rail row shows the selected highlight, decoupling a
 * Favorites copy from its canonical copy of the same resource.
 *
 * @param listItem When true (default), this element is a direct list sibling
 *   and carries list-item + selected attrs for contiguous chrome. Set false
 *   when a parent wrapper (e.g. canvas/chat drop zone) is the list item.
 */
export function useRailRowSelection(
  resourceId: string,
  section: RailSelectionSource,
  active: boolean,
  kind: RailSelectionKind,
  listItem = true,
) {
  const ctx = useContext(RailSelectionContext);
  const rowKey = railSelectionKey(section, kind, resourceId);

  const inMulti = ctx?.isSelected(rowKey) ?? false;
  const selectionEmpty = (ctx?.selectionCount ?? 0) === 0;

  const selected =
    inMulti || (selectionEmpty && active && section !== "favorites");

  const onActivate = useCallback(
    (e?: React.MouseEvent) => {
      if (!ctx) return;
      if (e) ctx.rowClick(rowKey, e);
      else ctx.selectOnly(rowKey);
    },
    [ctx, rowKey],
  );

  return {
    selected,
    onActivate,
    rowKey,
    selectOnly: () => ctx?.selectOnly(rowKey),
    selectionAttrs: {
      [RAIL_SELECTION_KEY_ATTR]: rowKey,
      [RAIL_CHROME_ATTR]: "",
      ...(listItem ? { [RAIL_LIST_ITEM_ATTR]: "" } : {}),
      ...(selected && listItem ? { [RAIL_SELECTED_ATTR]: "" } : {}),
    } as Record<string, string>,
  };
}

/** Keep a mounted row's resource resolvable for bulk actions / multi-drag. */
export function useRegisterRailResource(
  kind: RailSelectionKind,
  id: string,
  resource: RailRegisteredResource,
) {
  const ctx = useContext(RailSelectionContext);
  const register = ctx?.registerResource;
  const unregister = ctx?.unregisterResource;

  useEffect(() => {
    register?.(kind, id, resource);
    return () => unregister?.(kind, id);
  }, [register, unregister, kind, id, resource]);
}
